import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/landing/shared";
import { ApplicationForm } from "@/components/jobs/ApplicationForm";
import { SaveJobButton } from "@/components/candidate/SaveJobButton";

const API = process.env.REACT_APP_BACKEND_URL;

const PROCESS_STEPS = [
    "Apply online",
    "Application review",
    "Screening",
    "Employer interview",
    "Documentation & mobilization",
    "Deployment",
];

const Fact = ({ label, value }) =>
    value ? (
        <div className="flex items-baseline justify-between gap-4 border-b border-ink/10 py-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-ink/45">{label}</span>
            <span className="text-right text-sm font-medium text-ink">{value}</span>
        </div>
    ) : null;

export default function JobDetailPage() {
    const { slug } = useParams();
    const [job, setJob] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setJob(null);
        setNotFound(false);
        fetch(`${API}/api/jobs/${slug}`)
            .then((r) => {
                if (r.status === 404) {
                    setNotFound(true);
                    return null;
                }
                if (!r.ok) throw new Error();
                return r.json();
            })
            .then((d) => d && setJob(d))
            .catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!job) return;
        const prevTitle = document.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        const prevDesc = metaDesc?.getAttribute("content");
        document.title = `${job.title} — ${job.company} | Afrinexa Global Careers`;
        metaDesc?.setAttribute(
            "content",
            `Apply for ${job.title} at ${job.company} in ${job.city ? `${job.city}, ` : ""}${job.country}. ${job.employment_type}, ${job.positions} position${job.positions === 1 ? "" : "s"} — recruitment managed by Afrinexa Global.`
        );
        const employmentTypeMap = {
            "Full-time": "FULL_TIME", Contract: "CONTRACTOR",
            "Part-time": "PART_TIME", Temporary: "TEMPORARY",
        };
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.jobPosting = "true";
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            title: job.title,
            description: `${job.description} ${(job.requirements || []).join(" ")}`.trim(),
            hiringOrganization: { "@type": "Organization", name: job.company },
            jobLocation: {
                "@type": "Place",
                address: {
                    "@type": "PostalAddress",
                    addressLocality: job.city || undefined,
                    addressCountry: job.country,
                },
            },
            datePosted: (job.posted_at || "").slice(0, 10) || undefined,
            validThrough: job.deadline || undefined,
            employmentType: employmentTypeMap[job.employment_type] || undefined,
        });
        document.head.appendChild(script);
        return () => {
            document.title = prevTitle;
            if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
            script.remove();
        };
    }, [job]);

    if (notFound) {
        return (
            <div className="flex min-h-[70vh] flex-col items-center justify-center bg-ivory px-6 pt-24 text-center">
                <h1 className="font-serif text-3xl font-medium tracking-tight text-ink">
                    This role is no longer available
                </h1>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/60">
                    The position may have been filled or closed. Explore our current openings instead.
                </p>
                <a
                    href="/jobs"
                    data-testid="job-notfound-back"
                    className="mt-8 inline-flex items-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-ivory transition-colors hover:bg-forest-deep"
                >
                    View All Jobs
                    <ArrowRight size={16} strokeWidth={1.5} />
                </a>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ivory">
                <Loader2 size={24} className="animate-spin text-forest" />
            </div>
        );
    }

    return (
        <div data-testid="job-detail-page">
            <section className="bg-night pb-16 pt-40 text-ivory lg:pb-20 lg:pt-48">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <a href="/jobs" data-testid="job-back-link" className="text-xs font-medium uppercase tracking-[0.22em] text-ivory/55 transition-colors hover:text-champagne">
                        ← All Open Roles
                    </a>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-champagne">
                            <span className="h-px w-8 bg-champagne" aria-hidden="true" />
                            {job.category || "International Role"}
                        </p>
                        {job.is_demo && (
                            <span className="border border-ivory/25 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-ivory/55">
                                Demo listing
                            </span>
                        )}
                    </div>
                    <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
                        {job.title}
                    </h1>
                    <p className="mt-5 max-w-xl text-base leading-relaxed text-ivory/70">
                        {job.company} · {job.city ? `${job.city}, ` : ""}{job.country}
                    </p>
                </div>
            </section>

            <section className="bg-ivory py-16 lg:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
                        <Reveal className="lg:col-span-7">
                            <h2 className="font-serif text-2xl font-medium tracking-tight text-ink">
                                About This Role
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-ink/70">{job.description}</p>

                            {job.responsibilities?.length > 0 && (
                                <>
                                    <h3 className="mt-12 font-serif text-xl font-medium tracking-tight text-ink">
                                        Responsibilities
                                    </h3>
                                    <ul className="mt-5 space-y-3">
                                        {job.responsibilities.map((line) => (
                                            <li key={line} className="flex items-start gap-4 text-sm leading-relaxed text-ink/70">
                                                <span className="mt-2.5 h-px w-5 shrink-0 bg-champagne" aria-hidden="true" />
                                                {line}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {job.requirements?.length > 0 && (
                                <>
                                    <h3 className="mt-12 font-serif text-xl font-medium tracking-tight text-ink">
                                        Requirements
                                    </h3>
                                    <ul className="mt-5 space-y-3">
                                        {job.requirements.map((line) => (
                                            <li key={line} className="flex items-start gap-4 text-sm leading-relaxed text-ink/70">
                                                <span className="mt-2.5 h-px w-5 shrink-0 bg-champagne" aria-hidden="true" />
                                                {line}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {job.qualifications && (
                                <>
                                    <h3 className="mt-12 font-serif text-xl font-medium tracking-tight text-ink">
                                        Qualifications
                                    </h3>
                                    <p className="mt-5 text-sm leading-relaxed text-ink/70">{job.qualifications}</p>
                                </>
                            )}

                            {job.benefits && (
                                <>
                                    <h3 className="mt-12 font-serif text-xl font-medium tracking-tight text-ink">
                                        Benefits
                                    </h3>
                                    <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-ink/70">
                                        {job.benefits}
                                    </p>
                                </>
                            )}

                            <h3 className="mt-12 font-serif text-xl font-medium tracking-tight text-ink">
                                How the Recruitment Process Works
                            </h3>
                            <ol className="mt-5 space-y-0">
                                {PROCESS_STEPS.map((step, i) => (
                                    <li key={step} className="flex items-baseline gap-5 border-t border-ink/10 py-4">
                                        <span className="font-serif text-lg font-medium text-forest/35">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-sm text-ink/75">{step}</span>
                                    </li>
                                ))}
                            </ol>
                            <p className="mt-6 text-xs leading-relaxed text-ink/45">
                                To apply, complete the application form below with your details, CV and any
                                supporting documents. Afrinexa Global reviews every application and contacts
                                candidates whose profiles match the employer's requirements. Afrinexa Global
                                supports recruitment and mobility processes — employment and visa decisions
                                rest with the employer and the relevant authorities.
                            </p>
                        </Reveal>

                        <Reveal delay={0.1} className="lg:col-span-5">
                            <div className="lg:sticky lg:top-32">
                                <div className="border border-ink/10 bg-sand p-7 sm:p-8" data-testid="job-facts-card">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-forest">
                                        Role Facts
                                    </p>
                                    <div className="mt-4">
                                        <Fact label="Employer" value={job.company} />
                                        <Fact label="Location" value={`${job.city ? `${job.city}, ` : ""}${job.country}`} />
                                        <Fact label="Employment Type" value={job.employment_type} />
                                        <Fact label="Vacancies" value={`${job.positions}`} />
                                        <Fact label="Experience" value={job.experience} />
                                        <Fact label="Salary" value={job.salary_text} />
                                        <Fact label="Accommodation" value={job.accommodation} />
                                        <Fact label="Transportation" value={job.transportation} />
                                        <Fact label="Contract Duration" value={job.contract_duration} />
                                        <Fact label="Expected Start Date" value={job.start_date} />
                                        <Fact
                                            label="Application Deadline"
                                            value={
                                                job.deadline
                                                    ? new Date(job.deadline + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                                                    : ""
                                            }
                                        />
                                    </div>
                                    <a
                                        href="#apply"
                                        data-testid="job-apply-now-btn"
                                        className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-colors duration-300 hover:bg-forest-deep"
                                    >
                                        Apply Now
                                        <ArrowRight size={16} strokeWidth={1.5} />
                                    </a>
                                    <div className="mt-3 flex justify-center">
                                        <SaveJobButton jobId={job.id} />
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <div id="apply" className="mt-16 scroll-mt-28 lg:mt-24">
                        <Reveal>
                            <h2 className="font-serif text-2xl font-medium tracking-tight text-ink sm:text-3xl">
                                Apply for This Role
                            </h2>
                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink/60">
                                Complete the application below. Fields marked{" "}
                                <span className="text-champagne">*</span> are required.
                            </p>
                            <div className="mt-8">
                                <ApplicationForm job={job} />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
